import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import Hero from "./components/Hero";
import TrustBar from "./components/TrustBar";
import ProductCard from "./components/ProductCard";
import Newsletter from "./components/Newsletter";
import SafeImage from "../../components/SafeImage";
import { fetchProducts } from "../../lib/api";
import { useTemplate } from "../../context/TemplateContext";

/**
 * SRC-styled ecommerce home page — a near-literal port of the uploaded
 * STRVKT template. Section order (top→bottom):
 *   1. Hero (full-screen, transparent header overlaid)
 *   2. TrustBar (4-col guarantees under the hero)
 *   3. Featured drop (8 products, 2/3/4-col grid)
 *   4. Category tiles (4-col, aspect 4/5)
 *   5. Editorial banner (70vh, dark overlay, headline + CTA)
 *   6. Reviews (3-col, star rating)
 *   7. Newsletter (black/white split)
 *
 * All copy and images come from the template config so different store
 * types (clothing / skincare / coffee / candles / jewelry) share the exact
 * same layout with their own products, categories, and hero imagery.
 */
export default function Home() {
  const { config: cfg, basePath, slug } = useTemplate();
  const brand = cfg.brand || {};
  const accent = brand.accentColor || "#E60000";
  const copy = cfg.copy || {};
  const homeCopy = copy.home || {};
  const categoryTiles = (cfg.categoryTiles || cfg.categories || []).slice(0, 4);
  const editorial = homeCopy.editorial || {};
  const reviews = cfg.reviews || DEFAULT_REVIEWS;

  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ slug, featured: true, limit: 8, fallback: cfg.demoProducts })
      .then(setFeatured)
      .catch(() => {});
  }, [cfg.demoProducts, slug]);

  return (
    <div data-testid="home-page">
      <Hero config={cfg} basePath={basePath} />

      <TrustBar items={cfg.trustBar} />

      {/* 3. Featured Drop */}
      <section
        data-testid="featured-products"
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28"
      >
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500" data-testid="featured-eyebrow">
              {homeCopy.featuredEyebrow || "— Featured"}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none" data-testid="featured-headline">
              {homeCopy.featuredHeadline || "Featured pieces."}
            </h2>
          </div>
          <Link
            to={`${basePath}${homeCopy.viewAllTo || "/shop"}`}
            data-testid="view-all-link"
            className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline pb-1"
          >
            {homeCopy.viewAllLabel || "View all"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        <div
          data-testid="product-grid"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6"
        >
          {featured.map((p, i) => (
            <ProductCard key={p.id || p.slug} product={p} basePath={basePath} accent={accent} eager={i < 4} />
          ))}
        </div>
      </section>

      {/* 4. Category split */}
      <section className="border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20">
          <h2 className="font-display text-4xl sm:text-5xl mb-10 leading-none" data-testid="category-headline">
            {homeCopy.categoryHeadline || "Shop by category."}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categoryTiles.map((c) => (
              <Link
                key={c.key || c.slug}
                to={`${basePath}/shop?category=${c.key || c.slug}`}
                data-testid={`category-tile-${c.key || c.slug}`}
                className="group relative aspect-[4/5] overflow-hidden bg-neutral-100"
              >
                <SafeImage
                  src={c.img || c.image_url}
                  alt={c.title || c.label}
                  className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
                  style={{ objectFit: "cover" }}
                  fallbackLabel={c.title || c.label}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                  <p className="font-display text-2xl sm:text-3xl">{c.title || c.label}</p>
                  <ArrowRight className="w-5 h-5 translate-x-0 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Editorial banner */}
      <section className="relative h-[70vh] overflow-hidden">
        <SafeImage
          src={editorial.image}
          alt={editorial.headline || "Editorial"}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover" }}
          fallbackLabel={brand.name || "Store"}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center">
          <div className="max-w-xl text-white">
            <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-200" data-testid="editorial-eyebrow">
              {editorial.eyebrow || "— The Story"}
            </p>
            <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
              <span data-testid="editorial-headline">{editorial.headline || "Made small-batch."}</span>
              {editorial.headlineLine2 && <><br /><span data-testid="editorial-headline-line2">{editorial.headlineLine2}</span></>}
            </h3>
            <p className="mt-5 text-sm sm:text-base text-neutral-200 max-w-md leading-relaxed" data-testid="editorial-body">
              {editorial.body || "Every piece is designed and made in small batches. Details you won't find on shelves."}
            </p>
            <Link
              to={editorial.ctaLink ? `${basePath}${editorial.ctaLink}` : `${basePath}/shop`}
              className="mt-8 inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300"
              onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#000"; }}
            >
              <span data-testid="editorial-cta">{editorial.cta || "Shop the story"}</span> <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Reviews */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500" data-testid="reviews-eyebrow">
              {homeCopy.reviewsEyebrow || "— Real Customers"}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-none" data-testid="reviews-headline">
              {homeCopy.reviewsHeadline || "Loved by thousands."}
            </h2>
          </div>
          <p className="hidden sm:block text-sm text-neutral-500" data-testid="reviews-average">{homeCopy.reviewsAverageLine || "★ 4.8 / 5 average"}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6" data-testid="reviews-band">
          {reviews.slice(0, 3).map((r, i) => (
            <div key={i} className="border border-neutral-200 p-8 hover:border-black transition-colors">
              <div className="text-amber-500 text-sm">{"★".repeat(r.rating || 5)}</div>
              <p className="mt-4 text-base leading-relaxed">&ldquo;{r.text}&rdquo;</p>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-neutral-500">— {r.name}, Verified Buyer</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7. Newsletter */}
      <Newsletter config={cfg} slug={slug} />
    </div>
  );
}

const DEFAULT_REVIEWS = [
  { name: "Marcus T.", text: "Quality is on another level for the price. Will absolutely buy again.", rating: 5 },
  { name: "Jordan K.", text: "Fit is perfect, packaging felt premium, delivery was quick.", rating: 5 },
  { name: "Sasha L.", text: "Been searching for a brand that gets the fundamentals right — this is it.", rating: 5 },
];
